import React, { useState, useEffect, useCallback } from 'react';
import { X, Shield, UserCheck, Briefcase, Users, AlertCircle, CheckCircle2, Loader2, MapPin, Check, Database, FileSearch, Banknote, GraduationCap, School, BookOpen, Gift, Key, UserMinus } from 'lucide-react';
import { API } from '@/lib/api-client';
import type { AccessChange, AccessChangeNote } from '@/lib/api-client';
import { adminService } from '@/services/api/adminService';

interface UserRolesResponse {
  user_id: string;
  email: string;
  display_name: string;
  explicit_roles: string[];  // Manually assigned
  derived_roles: string[];   // Auto-derived
  all_roles: string[];       // Combined
  hrbp_functional_areas: string[];  // Functional areas for HRBP
  fno_admin_functional_areas: string[];  // Functional areas for FnO Admin
  data_team_is_approver: boolean;  // Whether this data_team member is a correction approver
}

/** One grantable capability, as served by the backend permission registry. */
interface PermissionSpec {
  key: string;
  label: string;
  description: string;
  group: string;
  /** The dimension a scope narrows this key by: 'department', 'campus', or
   *  null when it cannot be scoped. The server owns the mapping. */
  scope_type?: string | null;
}

interface UserRoleManagementProps {
  userId: string;
  userEmail: string;
  userName: string;
  onClose: () => void;
  onRoleChanged?: () => void;
}

const MANUAL_ROLES = [
  { name: 'admin', displayName: 'Administrator', icon: Shield, color: 'text-red-600', description: 'Full system access' },
  { name: 'hr_admin', displayName: 'HR Administrator', icon: Briefcase, color: 'text-blue-600', description: 'HR management access' },
  { name: 'hrbp', displayName: 'HR Business Partner', icon: Users, color: 'text-purple-600', description: 'Restricted admin access - Support only' },
  { name: 'fno_admin', displayName: 'F&O Administrator', icon: Database, color: 'text-orange-600', description: 'Restricted admin access - Data Corrections only' },
  { name: 'data_team', displayName: 'Data Quality Team', icon: FileSearch, color: 'text-teal-600', description: 'Data quality comparisons, corrections & exports' },
  { name: 'payroll_ops', displayName: 'Payroll Operations', icon: Banknote, color: 'text-emerald-600', description: 'Integration hub - Payroll task queue & drift remediation' },
  { name: 'rewarded_admin', displayName: 'Rewarded Administrator', icon: Gift, color: 'text-pink-600', description: 'Rewarded member sync - status, run history, snapshot & dry-run' },
  { name: 'academic_planner', displayName: 'Academic Planner', icon: GraduationCap, color: 'text-indigo-600', description: 'Lecturer resourcing - module/lecturer matching & coverage' },
  { name: 'dean', displayName: 'Dean', icon: School, color: 'text-sky-600', description: 'Faculty leadership - resourcing oversight & allocation sign-off' },
  { name: 'faculty_head', displayName: 'Faculty / Programme Head', icon: BookOpen, color: 'text-cyan-600', description: 'Faculty/programme head - resourcing oversight & sign-off' },
];

const AUTO_DERIVED_ROLES = [
  { name: 'employee', displayName: 'Team Member', icon: UserCheck, color: 'text-gray-600', description: 'Basic team member access' },
  { name: 'manager', displayName: 'People Leader', icon: Users, color: 'text-green-600', description: 'Team leadership access' }
];

/** A reason this short says nothing an auditor can use. */
const MIN_CHANGE_REASON = 5;

const CHANGE_WORDS: Record<string, string> = {
  role_assigned: 'Role given',
  role_removed: 'Role removed',
  grant_added: 'Permission granted',
  grant_removed: 'Permission removed',
  scope_added: 'Limit added',
  scope_removed: 'Limit removed',
  area_added: 'Area added',
  area_removed: 'Area removed',
};

/** What a trail entry changed, in the words the panel uses. */
function describeItem(h: AccessChange, catalog: { key: string; label: string }[] = []): string {
  if (h.change.startsWith('role')) return h.role_name || '';
  if (h.change.startsWith('area')) return `${h.role_name}: ${h.scope_value}`;
  const label = catalog.find((p) => p.key === h.permission_key)?.label || h.permission_key || '';
  return h.change.startsWith('scope') ? `${label} (${h.scope_value})` : label;
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', dateStyle: 'medium', timeStyle: 'short' });
}

export const UserRoleManagement: React.FC<UserRoleManagementProps> = ({
  userId,
  userEmail,
  userName,
  onClose,
  onRoleChanged
}) => {
  const [userRoles, setUserRoles] = useState<UserRolesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Functional area state — keyed by role name for independent HRBP/FnO Admin selections
  const [availableFunctionalAreas, setAvailableFunctionalAreas] = useState<string[]>([]);
  const [selectedAreasByRole, setSelectedAreasByRole] = useState<Record<string, string[]>>({ hrbp: [], fno_admin: [] });
  const [faLoading, setFaLoading] = useState(false);
  const [faSaving, setFaSaving] = useState(false);

  // Data team approver toggle
  const [dataTeamIsApprover, setDataTeamIsApprover] = useState(false);
  const [approverSaving, setApproverSaving] = useState(false);

  // Performance-cycle exclusion — for people F&O classifies correctly (e.g. FTC,
  // where changing the category would change their pay) who are nonetheless not
  // expected to do objectives / check-ins / calibration.
  const [reviewExcluded, setReviewExcluded] = useState(false);
  const [exclusionReason, setExclusionReason] = useState('');
  const [exclusionMeta, setExclusionMeta] = useState<{ by: string | null; at: string | null }>({ by: null, at: null });
  const [exclusionSaving, setExclusionSaving] = useState(false);
  const [showExclusionForm, setShowExclusionForm] = useState(false);

  // Direct capability grants — independent of roles. Lets one person be given
  // one capability (e.g. budget:read) without making them an admin.
  const [permCatalog, setPermCatalog] = useState<PermissionSpec[]>([]);
  const [grantedPerms, setGrantedPerms] = useState<string[]>([]);
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState<string | null>(null);

  // The access-change trail: every save below records why, and on what
  // reference, so a reason is asked for before anything can change. Both are
  // cleared when the panel moves to another person, so a reason never carries
  // over to the wrong subject.
  const [changeReason, setChangeReason] = useState('');
  const [changeReference, setChangeReference] = useState('');
  const [history, setHistory] = useState<AccessChange[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const reasonGiven = changeReason.trim().length >= MIN_CHANGE_REASON;
  const changeNote = (): AccessChangeNote => ({
    reason: changeReason.trim(),
    reference: changeReference.trim() || null,
  });

  useEffect(() => {
    setChangeReason('');
    setChangeReference('');
    setHistory([]);
    loadUserRoles();
    loadPermissions();
    loadReviewExclusion();
    loadHistory();
  }, [userId]);

  const loadHistory = async () => {
    try {
      const r = await API.users.getAccessHistory(userId);
      setHistory(r.entries || []);
    } catch (err) {
      // Non-fatal: changes still save and are still recorded.
      console.error('Failed to load access history:', err);
    }
  };

  /** False, with a message, until a reason for the changes is given. */
  const needReason = (): boolean => {
    if (reasonGiven) return true;
    setError(`Give a reason for these changes first (at least ${MIN_CHANGE_REASON} characters), at the top.`);
    setTimeout(() => setError(null), 4000);
    return false;
  };

  const loadReviewExclusion = async () => {
    try {
      const r = await API.users.getReviewExclusion(userId);
      setReviewExcluded(r.review_excluded);
      setExclusionReason(r.reason || '');
      setExclusionMeta({ by: r.excluded_by, at: r.excluded_at });
    } catch (err) {
      // Non-fatal — the roles panel stays usable.
      console.error('Failed to load review exclusion:', err);
    }
  };

  const saveReviewExclusion = async (next: boolean) => {
    if (next && !exclusionReason.trim()) {
      setError('Please give a reason before excluding someone from the cycle');
      setTimeout(() => setError(null), 4000);
      return;
    }
    setExclusionSaving(true);
    try {
      const r = await API.users.updateReviewExclusion(userId, next, exclusionReason.trim());
      setReviewExcluded(r.review_excluded);
      setExclusionReason(r.reason || '');
      setExclusionMeta({ by: r.excluded_by, at: r.excluded_at });
      setShowExclusionForm(false);
      setSuccess(next
        ? 'Excluded from the performance cycle. Existing objectives and check-ins are kept.'
        : 'Returned to the performance cycle.');
      setTimeout(() => setSuccess(null), 3500);
    } catch (err: any) {
      setError(err.message || 'Failed to update performance-cycle exclusion');
      setTimeout(() => setError(null), 4000);
    } finally {
      setExclusionSaving(false);
    }
  };

  // Department scopes. A key absent from `scopes` is organisation-wide;
  // `scopableKeys` is the server's list of keys it can actually filter, so the
  // UI never offers a restriction the endpoints would ignore.
  const [scopes, setScopes] = useState<Record<string, string[]>>({});
  const [scopableKeys, setScopableKeys] = useState<string[]>([]);
  const [deptOptions, setDeptOptions] = useState<{ code: string; positions: number; vacant: number }[]>([]);
  // The financial dimension campuses a talent:campus_input grant can name. The
  // dimension has no name of its own, so each code carries where most of its
  // people sit (Victor, 9 Sep 2026).
  const [campusOptions, setCampusOptions] = useState<{ code: string; people: number; mostly: string | null }[]>([]);
  // The functions, and departments within them, a talent:dashboard grant can be
  // narrowed to (Victor and HR, 9 Oct 2026: a function head sees their function;
  // Academics splits one level down by department).
  const [areaOptions, setAreaOptions] = useState<{
    name: string;
    value: string;
    positions: number;
    departments: { name: string; value: string; positions: number }[];
  }[]>([]);
  const [scopeSaving, setScopeSaving] = useState<string | null>(null);

  // The scope is a property of the PERSON, not of each capability: "Sam can
  // see IT" reads the way people actually think about it, and every scopable
  // grant he holds is limited the same way. The table underneath is still keyed
  // per permission, so a future need for per-capability scopes costs no
  // migration - the UI just stops writing the same set to every key.
  /** The keys this person holds that are narrowed by one dimension. */
  const heldKeysOf = React.useCallback(
    (scopeType: string, granted?: string[]) =>
      scopableKeys.filter(k =>
        (granted || grantedPerms).includes(k) &&
        (permCatalog.find(p => p.key === k)?.scope_type || 'department') === scopeType),
    [scopableKeys, grantedPerms, permCatalog],
  );

  /** The values currently narrowing one dimension; empty means everything. */
  const scopeValuesOf = React.useCallback((scopeType: string) => {
    for (const k of heldKeysOf(scopeType)) {
      if ((scopes[k] || []).length) return scopes[k];
    }
    return [];
  }, [scopes, heldKeysOf]);

  const departments = React.useMemo(() => scopeValuesOf('department'), [scopeValuesOf]);
  const campuses = React.useMemo(() => scopeValuesOf('campus'), [scopeValuesOf]);
  const areas = React.useMemo(() => scopeValuesOf('talent_area'), [scopeValuesOf]);

  /** Write `next` to every grant this person holds that the dimension narrows.
   *  Each dimension is written independently, so limiting someone to a campus
   *  never touches the departments their budget access is limited to. */
  const writeScopes = async (next: string[], scopeType = 'department', grantedOverride?: string[]) => {
    const granted = grantedOverride || grantedPerms;
    const payload = scopableKeys
      .filter(k => granted.includes(k))
      .map(k => {
        const type = permCatalog.find(p => p.key === k)?.scope_type || 'department';
        return {
          permission_key: k,
          scope_type: type,
          scope_values: type === scopeType ? next : (scopes[k] || []),
        };
      });
    const res = await API.users.updateUserPermissionScopes(userId, payload, changeNote());
    const rebuilt: Record<string, string[]> = {};
    (res.scopes || []).forEach(sc => { rebuilt[sc.permission_key] = sc.scope_values; });
    setScopes(rebuilt);
  };

  // The group whose capabilities the scope narrows — the selector renders
  // there and nowhere else, so there is only ever one control writing it.
  const firstScopableGroup = React.useMemo(() => {
    const p = permCatalog.find(x => scopableKeys.includes(x.key) && (x.scope_type || 'department') === 'department');
    return p?.group;
  }, [permCatalog, scopableKeys]);

  /** The group holding the campus-scopable key, and whether this person has it. */
  const campusScopableGroup = React.useMemo(
    () => permCatalog.find(x => scopableKeys.includes(x.key) && x.scope_type === 'campus')?.group,
    [permCatalog, scopableKeys],
  );
  const campusScopableHeld = React.useMemo(
    () => heldKeysOf('campus').length > 0,
    [heldKeysOf],
  );

  /** The group holding the area-scopable key (the talent dashboard), and whether this person has it. */
  const areaScopableGroup = React.useMemo(
    () => permCatalog.find(x => scopableKeys.includes(x.key) && x.scope_type === 'talent_area')?.group,
    [permCatalog, scopableKeys],
  );
  const areaScopableHeld = React.useMemo(
    () => heldKeysOf('talent_area').length > 0,
    [heldKeysOf],
  );

  const toggleScope = async (scopeType: string, code: string, everythingLabel: string) => {
    if (!needReason()) return;
    const current = scopeValuesOf(scopeType);
    const next = current.includes(code) ? current.filter(c => c !== code) : [...current, code].sort();
    const previous = scopes;
    const held = heldKeysOf(scopeType);
    setScopes({ ...scopes, ...Object.fromEntries(held.map(k => [k, next])) });   // optimistic
    setScopeSaving('*');
    try {
      await writeScopes(next, scopeType);
      void loadHistory();
      setSuccess(next.length ? `Limited to ${next.join(', ')}` : everythingLabel);
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setScopes(previous);
      setError(err.message || 'Failed to update the limit');
      setTimeout(() => setError(null), 4000);
    } finally {
      setScopeSaving(null);
    }
  };

  const toggleDepartment = (code: string) => toggleScope('department', code, 'Widened to all departments');
  const toggleCampus = (code: string) => toggleScope('campus', code, 'Widened to all campuses');
  const toggleArea = (value: string) => toggleScope('talent_area', value, 'Widened to the whole organisation');

  const loadPermissions = async () => {
    try {
      setPermLoading(true);
      const [catalog, granted, scopeRes, depts, campusRes, areaRes] = await Promise.all([
        API.users.getPermissionCatalog(),
        API.users.getUserPermissions(userId),
        API.users.getUserPermissionScopes(userId),
        API.users.getDepartmentScopeOptions(),
        API.users.getCampusScopeOptions().catch(() => ({ campuses: [] })),
        API.users.getTalentAreaScopeOptions().catch(() => ({ functions: [] })),
      ]);
      setPermCatalog(catalog.permissions || []);
      setGrantedPerms(granted.permissions || []);
      const built: Record<string, string[]> = {};
      (scopeRes.scopes || []).forEach(sc => { built[sc.permission_key] = sc.scope_values; });
      setScopes(built);
      setScopableKeys(scopeRes.scopable_keys || []);
      setDeptOptions(depts.departments || []);
      setCampusOptions(campusRes.campuses || []);
      setAreaOptions(areaRes.functions || []);
    } catch (err: any) {
      // Non-fatal: the roles panel is still usable without the grants section.
      console.error('Failed to load permission grants:', err);
    } finally {
      setPermLoading(false);
    }
  };

  const togglePermission = async (key: string) => {
    if (!needReason()) return;
    const next = grantedPerms.includes(key)
      ? grantedPerms.filter(k => k !== key)
      : [...grantedPerms, key];
    const previous = grantedPerms;
    setGrantedPerms(next);          // optimistic
    setPermSaving(key);
    try {
      const res = await API.users.updateUserPermissions(userId, next, changeNote());
      setGrantedPerms(res.permissions || next);
      // A capability granted while a limit is in force inherits it, on its own
      // dimension. Without this the new grant would silently be
      // organisation-wide, the opposite of what the person setting the limit
      // intended. A campus grant inherits the campus limit, not the department.
      if (scopableKeys.includes(key)) {
        const type = permCatalog.find(p => p.key === key)?.scope_type || 'department';
        const inForce = type === 'campus' ? campuses : type === 'talent_area' ? areas : departments;
        if (inForce.length) await writeScopes(inForce, type, next);
      }
      void loadHistory();
      setSuccess(
        next.includes(key)
          ? `Granted "${permCatalog.find(p => p.key === key)?.label || key}"`
          : `Removed "${permCatalog.find(p => p.key === key)?.label || key}"`
      );
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setGrantedPerms(previous);    // revert
      setError(err.message || 'Failed to update permission');
      setTimeout(() => setError(null), 4000);
    } finally {
      setPermSaving(null);
    }
  };

  // Load available functional areas when HRBP or FnO Admin role is present
  useEffect(() => {
    if (userRoles?.all_roles.includes('hrbp') || userRoles?.all_roles.includes('fno_admin')) {
      loadFunctionalAreas();
    }
  }, [userRoles?.all_roles]);

  const loadUserRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const roles = await API.users.getRoles(userId);
      setUserRoles(roles);
      // Set selected functional areas per role from the response
      setSelectedAreasByRole({
        hrbp: roles.hrbp_functional_areas || [],
        fno_admin: roles.fno_admin_functional_areas || [],
      });
      setDataTeamIsApprover(roles.data_team_is_approver || false);
    } catch (err: any) {
      setError(err.message || 'Failed to load user roles');
    } finally {
      setLoading(false);
    }
  };

  const loadFunctionalAreas = async () => {
    try {
      setFaLoading(true);
      const data = await adminService.getFunctionalAreaBreakdown();
      const areas = (data.functional_areas || [])
        .map(a => a.functional_area)
        .filter(a => a && a !== 'Uncategorized')
        .sort();
      setAvailableFunctionalAreas(areas);
    } catch (err: any) {
      console.error('Failed to load functional areas:', err);
      // Non-critical — selector just won't show options
    } finally {
      setFaLoading(false);
    }
  };

  const handleToggleFunctionalArea = useCallback(async (area: string, roleName: string) => {
    if (!needReason()) return;
    const currentSelection = selectedAreasByRole[roleName] || [];
    const newSelection = currentSelection.includes(area)
      ? currentSelection.filter(a => a !== area)
      : [...currentSelection, area];

    setSelectedAreasByRole(prev => ({ ...prev, [roleName]: newSelection }));

    try {
      setFaSaving(true);
      setError(null);
      await API.users.updateHRBPFunctionalAreas(userId, newSelection, roleName, changeNote());
      void loadHistory();
    } catch (err: any) {
      setError(err.message || 'Failed to update functional areas');
      // Revert on error
      setSelectedAreasByRole(prev => ({ ...prev, [roleName]: currentSelection }));
    } finally {
      setFaSaving(false);
    }
  }, [selectedAreasByRole, userId, changeReason, changeReference]);

  const handleAssignRole = async (roleName: string) => {
    if (!needReason()) return;
    try {
      setActionLoading(roleName);
      setError(null);
      setSuccess(null);
      await API.users.assignRole(userId, roleName, changeNote());
      setSuccess(`Role '${roleName}' assigned successfully`);
      await loadUserRoles();
      void loadHistory();
      onRoleChanged?.();
    } catch (err: any) {
      setError(err.message || `Failed to assign role '${roleName}'`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnassignRole = async (roleName: string) => {
    if (!needReason()) return;
    try {
      setActionLoading(roleName);
      setError(null);
      setSuccess(null);
      await API.users.unassignRole(userId, roleName, changeNote());
      setSuccess(`Role '${roleName}' removed successfully`);
      await loadUserRoles();
      void loadHistory();
      onRoleChanged?.();
    } catch (err: any) {
      setError(err.message || `Failed to remove role '${roleName}'`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleDataTeamApprover = useCallback(async () => {
    if (!needReason()) return;
    const newValue = !dataTeamIsApprover;
    setDataTeamIsApprover(newValue);
    try {
      setApproverSaving(true);
      setError(null);
      await API.users.updateHRBPFunctionalAreas(
        userId,
        newValue ? ['approver'] : [],
        'data_team',
        changeNote(),
      );
      void loadHistory();
    } catch (err: any) {
      setError(err.message || 'Failed to update approver designation');
      setDataTeamIsApprover(!newValue); // Revert on error
    } finally {
      setApproverSaving(false);
    }
  }, [dataTeamIsApprover, userId, changeReason, changeReference]);

  const hasRole = (roleName: string): boolean => {
    return userRoles?.all_roles.includes(roleName) || false;
  };

  const isExplicitRole = (roleName: string): boolean => {
    return userRoles?.explicit_roles.includes(roleName) || false;
  };

  const isDerivedRole = (roleName: string): boolean => {
    return userRoles?.derived_roles.includes(roleName) || false;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Manage User Roles</h2>
            <p className="text-sm text-gray-600 mt-1">{userName} ({userEmail})</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Error</p>
                <p className="text-sm text-red-600 mt-1">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">Success</p>
                <p className="text-sm text-green-600 mt-1">{success}</p>
              </div>
            </div>
          )}

          {/* Why: recorded with every change below (the access-change trail). */}
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
            <div>
              <p className="text-sm font-medium text-amber-900">Why are you changing this person's access?</p>
              <p className="text-xs text-amber-800 mt-1">
                Every role, permission, limit and area you change here is recorded with this reason, who made the
                change and when. Nothing can be changed until a reason is given.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-2">
                <label htmlFor="access-change-reason" className="block text-xs font-medium text-gray-700 mb-1">
                  Reason for these changes
                </label>
                <input
                  id="access-change-reason"
                  value={changeReason}
                  maxLength={1000}
                  onChange={(e) => setChangeReason(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue"
                />
              </div>
              <div>
                <label htmlFor="access-change-reference" className="block text-xs font-medium text-gray-700 mb-1">
                  Reference (optional)
                </label>
                <input
                  id="access-change-reference"
                  value={changeReference}
                  maxLength={100}
                  placeholder="Ticket or e-mail"
                  onChange={(e) => setChangeReference(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-heritage-blue"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-heritage-blue" />
            </div>
          ) : (
            <>
              {/* Manual Roles Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Manual Role Assignment</h3>
                <p className="text-sm text-gray-600 mb-4">
                  These roles can be assigned or removed by administrators.
                </p>
                <div className="space-y-3">
                  {MANUAL_ROLES.map((role) => {
                    const Icon = role.icon;
                    const hasThisRole = hasRole(role.name);
                    const isExplicit = isExplicitRole(role.name);
                    const isLoading = actionLoading === role.name;
                    const showFunctionalAreas = (role.name === 'hrbp' || role.name === 'fno_admin') && hasThisRole;
                    const showDataTeamApprover = role.name === 'data_team' && hasThisRole;
                    const hasSubPanel = showFunctionalAreas || showDataTeamApprover;

                    return (
                      <div key={role.name} className="space-y-0">
                        <div
                          className={`border border-gray-200 p-4 flex items-center justify-between hover:border-gray-300 transition-colors ${
                            hasSubPanel ? 'rounded-t-lg border-b-0' : 'rounded-lg'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <Icon className={`h-5 w-5 ${role.color} flex-shrink-0 mt-0.5`} />
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900">{role.displayName}</p>
                                {hasThisRole && (
                                  <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded">
                                    Assigned
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                            </div>
                          </div>
                          <div>
                            {isLoading ? (
                              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                            ) : hasThisRole && isExplicit ? (
                              <button
                                onClick={() => handleUnassignRole(role.name)}
                                className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                              >
                                Remove
                              </button>
                            ) : !hasThisRole ? (
                              <button
                                onClick={() => handleAssignRole(role.name)}
                                className="px-4 py-2 text-sm font-medium text-heritage-blue bg-heritage-blue/10 hover:bg-heritage-blue/20 rounded-lg transition-colors"
                              >
                                Assign
                              </button>
                            ) : null}
                          </div>
                        </div>

                        {/* HRBP / FnO Admin Functional Area Selector */}
                        {showFunctionalAreas && (
                          <div className={`border border-gray-200 rounded-b-lg p-4 ${role.name === 'fno_admin' ? 'bg-orange-50/50' : 'bg-purple-50/50'}`}>
                            <div className="flex items-center gap-2 mb-3">
                              <MapPin className={`h-4 w-4 ${role.name === 'fno_admin' ? 'text-orange-600' : 'text-purple-600'}`} />
                              <p className="text-sm font-medium text-gray-900">Functional Areas</p>
                              {faSaving && (
                                <Loader2 className={`h-3 w-3 animate-spin ${role.name === 'fno_admin' ? 'text-orange-600' : 'text-purple-600'}`} />
                              )}
                              {(selectedAreasByRole[role.name] || []).length > 0 && (
                                <span className={`px-2 py-0.5 text-xs font-medium rounded ${role.name === 'fno_admin' ? 'bg-orange-100 text-orange-700' : 'bg-purple-100 text-purple-700'}`}>
                                  {(selectedAreasByRole[role.name] || []).length} selected
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mb-3">
                              Select the functional areas this {role.name === 'fno_admin' ? 'F&O Administrator' : 'HRBP'} oversees.
                            </p>
                            {faLoading ? (
                              <div className="flex items-center gap-2 py-2">
                                <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                                <span className="text-sm text-gray-500">Loading functional areas...</span>
                              </div>
                            ) : availableFunctionalAreas.length === 0 ? (
                              <p className="text-sm text-gray-500 py-2">No functional areas available.</p>
                            ) : (
                              <div className="grid grid-cols-2 gap-2">
                                {availableFunctionalAreas.map((area) => {
                                  const isSelected = (selectedAreasByRole[role.name] || []).includes(area);
                                  const accentSelected = role.name === 'fno_admin'
                                    ? 'border-orange-300 bg-orange-100 text-orange-800 font-medium'
                                    : 'border-purple-300 bg-purple-100 text-purple-800 font-medium';
                                  const accentHover = role.name === 'fno_admin'
                                    ? 'border-gray-200 bg-white text-gray-700 hover:border-orange-200 hover:bg-orange-50'
                                    : 'border-gray-200 bg-white text-gray-700 hover:border-purple-200 hover:bg-purple-50';
                                  const accentCheck = role.name === 'fno_admin'
                                    ? 'bg-orange-600 border-orange-600'
                                    : 'bg-purple-600 border-purple-600';
                                  return (
                                    <button
                                      key={area}
                                      onClick={() => handleToggleFunctionalArea(area, role.name)}
                                      disabled={faSaving}
                                      className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors text-left ${
                                        isSelected ? accentSelected : accentHover
                                      } ${faSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                    >
                                      <div className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                                        isSelected ? accentCheck : 'border-gray-300'
                                      }`}>
                                        {isSelected && <Check className="h-3 w-3 text-white" />}
                                      </div>
                                      <span className="truncate">{area}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Data Team Approver Toggle */}
                        {showDataTeamApprover && (
                          <div className="border border-gray-200 rounded-b-lg p-4 bg-teal-50/50">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <CheckCircle2 className="h-4 w-4 text-teal-600 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">Correction Approver</p>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    Designate this user as a required approver for data correction batches.
                                    All designated approvers must approve before a batch can be executed.
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                                {approverSaving && <Loader2 className="h-4 w-4 animate-spin text-teal-600" />}
                                <button
                                  onClick={handleToggleDataTeamApprover}
                                  disabled={approverSaving}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                    dataTeamIsApprover ? 'bg-teal-600' : 'bg-gray-300'
                                  } ${approverSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      dataTeamIsApprover ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Auto-Derived Roles Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Automatically Derived Roles</h3>
                <p className="text-sm text-gray-600 mb-4">
                  These roles are automatically assigned based on job family and organisational hierarchy. They cannot be manually changed.
                </p>
                <div className="space-y-3">
                  {AUTO_DERIVED_ROLES.map((role) => {
                    const Icon = role.icon;
                    const hasThisRole = hasRole(role.name);
                    const isDerived = isDerivedRole(role.name);

                    return (
                      <div
                        key={role.name}
                        className="border border-gray-200 rounded-lg p-4 flex items-center justify-between bg-gray-50"
                      >
                        <div className="flex items-start gap-3">
                          <Icon className={`h-5 w-5 ${role.color} flex-shrink-0 mt-0.5`} />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900">{role.displayName}</p>
                              {hasThisRole && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                                  Auto-Assigned
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                            {role.name === 'manager' && (
                              <p className="text-xs text-gray-500 mt-1">
                                Based on: Direct reports or managerial job family
                              </p>
                            )}
                            {role.name === 'employee' && (
                              <p className="text-xs text-gray-500 mt-1">
                                Based on: All users get this role by default
                              </p>
                            )}
                          </div>
                        </div>
                        {hasThisRole && isDerived && (
                          <div className="text-sm text-gray-500 font-medium">
                            System Managed
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Current Roles Summary */}
              <div className="border-t border-gray-200 pt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Current Active Roles</h4>
                <div className="flex flex-wrap gap-2">
                  {userRoles?.all_roles.map((roleName) => (
                    <span
                      key={roleName}
                      className="px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-full"
                    >
                      {roleName}
                    </span>
                  ))}
                </div>
              </div>

              {/* Performance-cycle exclusion */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-start gap-2 mb-1">
                  <UserMinus className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700">Performance Cycle</h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Use this when F&amp;O has someone classified correctly but they
                      aren&apos;t expected to take part — a temp on a fixed-term contract,
                      for example, where changing the F&amp;O category would change how
                      they&apos;re paid. Excluding removes them from objectives, check-ins
                      and calibration, and from the completion reports.
                      <span className="block mt-1 text-gray-400">
                        Existing objectives and check-ins are kept — this only changes
                        what&apos;s expected going forward.
                      </span>
                    </p>
                  </div>
                </div>

                {reviewExcluded ? (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-amber-900">
                          Excluded from the performance cycle
                        </p>
                        {exclusionReason && (
                          <p className="text-xs text-amber-800 mt-1">&ldquo;{exclusionReason}&rdquo;</p>
                        )}
                        {(exclusionMeta.by || exclusionMeta.at) && (
                          <p className="text-xs text-amber-700/80 mt-1">
                            {exclusionMeta.by ? `by ${exclusionMeta.by}` : ''}
                            {exclusionMeta.at ? ` on ${new Date(exclusionMeta.at).toLocaleDateString('en-ZA')}` : ''}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => saveReviewExclusion(false)}
                        disabled={exclusionSaving}
                        className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 disabled:opacity-60"
                      >
                        {exclusionSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Return to cycle'}
                      </button>
                    </div>
                  </div>
                ) : showExclusionForm ? (
                  <div className="mt-3 rounded-lg border border-gray-200 p-3 space-y-2">
                    <label className="block text-xs font-medium text-gray-700">
                      Reason (required — so this stays explicable later)
                    </label>
                    <input
                      type="text"
                      value={exclusionReason}
                      onChange={(e) => setExclusionReason(e.target.value)}
                      placeholder="e.g. Temp per Tommie — FTC in F&O for payroll purposes"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-heritage-blue/30"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => saveReviewExclusion(true)}
                        disabled={exclusionSaving || !exclusionReason.trim()}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
                      >
                        {exclusionSaving ? 'Saving...' : 'Exclude from cycle'}
                      </button>
                      <button
                        onClick={() => { setShowExclusionForm(false); setExclusionReason(''); }}
                        className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowExclusionForm(true)}
                    className="mt-3 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Exclude from performance cycle
                  </button>
                )}
              </div>

              {/* Direct permissions — one capability at a time, no role needed */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-start gap-2 mb-1">
                  <Key className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700">Direct Permissions</h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Give this person a single capability without granting a full role —
                      for example Budget access for a Head of Finance. These add to whatever
                      their roles already allow and are saved immediately.
                    </p>
                  </div>
                </div>

                {permLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading permissions...
                  </div>
                ) : permCatalog.length === 0 ? (
                  <p className="text-sm text-gray-400 py-3">No grantable permissions are defined.</p>
                ) : (
                  <div className="mt-3 space-y-4">
                    {Array.from(new Set(permCatalog.map(p => p.group))).map((group) => (
                      <div key={group}>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          {group}
                        </p>
                        <div className="space-y-1.5">
                          {permCatalog.filter(p => p.group === group).map((perm) => {
                            const granted = grantedPerms.includes(perm.key);
                            const saving = permSaving === perm.key;
                            return (
                              <div key={perm.key}>
                              <button
                                onClick={() => togglePermission(perm.key)}
                                disabled={!!permSaving}
                                className={`w-full text-left flex items-start gap-3 px-3 py-2 rounded-lg border transition-colors disabled:opacity-60 ${
                                  granted
                                    ? 'border-heritage-blue/40 bg-heritage-blue/[0.04]'
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`}
                              >
                                <span
                                  className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                                    granted ? 'bg-heritage-blue border-heritage-blue' : 'border-gray-300'
                                  }`}
                                >
                                  {saving
                                    ? <Loader2 className="w-3 h-3 animate-spin text-gray-500" />
                                    : granted && <Check className="w-3 h-3 text-white" />}
                                </span>
                                <span className="min-w-0">
                                  <span className="block text-sm font-medium text-gray-800">{perm.label}</span>
                                  <span className="block text-xs text-gray-500">{perm.description}</span>
                                </span>
                              </button>

                              </div>
                            );
                          })}
                        </div>

                        {/* The department limit lives with the capabilities it
                            actually narrows, rather than floating above every
                            group as if it applied to all of them. Only Budget
                            has scopable keys today; if another group gains one
                            this follows it, and the `firstScopable` guard keeps
                            a single selector writing the person-level scope. */}
                        {group === firstScopableGroup && deptOptions.length > 0 && (
                          <div className="mt-2 p-3 rounded-lg border border-gray-200 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-gray-700">
                                Limit to departments
                                {scopeSaving === '*' && (
                                  <Loader2 className="w-3 h-3 animate-spin inline ml-2 text-gray-500" />
                                )}
                              </p>
                              <span className="text-xs text-gray-500">
                                {departments.length === 0
                                  ? 'Whole organisation'
                                  : `${departments.length} selected`}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {deptOptions.map(d => {
                                const on = departments.includes(d.code);
                                return (
                                  <button
                                    key={d.code}
                                    onClick={() => toggleDepartment(d.code)}
                                    disabled={scopeSaving === '*'}
                                    title={`${d.positions} positions`}
                                    className={`px-2 py-1 rounded text-xs border transition-colors disabled:opacity-60 ${
                                      on
                                        ? 'bg-heritage-blue text-white border-heritage-blue'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                    }`}
                                  >
                                    {d.code}
                                  </button>
                                );
                              })}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-2">
                              Choosing none leaves the whole organisation visible. Codes are
                              F&amp;O financial dimensions, not Role Master department names.
                            </p>
                          </div>
                        )}

                        {/* The campus limit, for the Annual I&G Review's campus
                            input space. Its codes are the F&O financial
                            dimension campus, which is what the organisation
                            accounts a person under — not the directory campus,
                            which for two General Managers reads "Support" while
                            they answer for different campuses entirely. */}
                        {group === campusScopableGroup && campusScopableHeld && campusOptions.length > 0 && (
                          <div className="mt-2 p-3 rounded-lg border border-gray-200 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-gray-700">
                                Limit to campuses
                                {scopeSaving === '*' && (
                                  <Loader2 className="w-3 h-3 animate-spin inline ml-2 text-gray-500" />
                                )}
                              </p>
                              <span className="text-xs text-gray-500">
                                {campuses.length === 0 ? 'Every campus' : `${campuses.length} selected`}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {campusOptions.map(c => {
                                const on = campuses.includes(c.code);
                                return (
                                  <button
                                    key={c.code}
                                    onClick={() => toggleCampus(c.code)}
                                    disabled={scopeSaving === '*'}
                                    title={`${c.people} people${c.mostly ? `, mostly ${c.mostly}` : ''}`}
                                    className={`px-2 py-1 rounded text-xs border transition-colors disabled:opacity-60 ${
                                      on
                                        ? 'bg-heritage-blue text-white border-heritage-blue'
                                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                                    }`}
                                  >
                                    {c.code}
                                    {c.mostly ? <span className="opacity-70"> · {c.mostly}</span> : null}
                                  </button>
                                );
                              })}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-2">
                              Choosing none leaves every campus visible. Codes are the F&amp;O
                              financial dimension campus, not the directory campus on the person.
                            </p>
                          </div>
                        )}

                        {/* The talent dashboard's limit: a whole function, or a
                            department within one (HR, 9 Oct 2026: a function head
                            sees their function; Academics splits by department). */}
                        {group === areaScopableGroup && areaScopableHeld && areaOptions.length > 0 && (
                          <div className="mt-2 p-3 rounded-lg border border-gray-200 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-gray-700">
                                Limit the talent dashboard to functions or departments
                                {scopeSaving === '*' && (
                                  <Loader2 className="w-3 h-3 animate-spin inline ml-2 text-gray-500" />
                                )}
                              </p>
                              <span className="text-xs text-gray-500">
                                {areas.length === 0 ? null : `${areas.length} selected`}
                              </span>
                            </div>
                            {/* Unscoped means everything, as for every scoped grant: said
                                plainly so nobody hands out the whole organisation by
                                leaving the picker alone (coordinator review, 9 Oct 2026). */}
                            {areas.length === 0 && (
                              <p className="mb-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                                No area chosen: they see the whole organisation (without gender or race).
                              </p>
                            )}
                            <div className="space-y-2">
                              {areaOptions.map(fn => {
                                const whole = areas.includes(fn.value);
                                return (
                                  <div key={fn.value}>
                                    <button
                                      onClick={() => toggleArea(fn.value)}
                                      disabled={scopeSaving === '*'}
                                      title={`${fn.positions} positions`}
                                      className={`px-2 py-1 rounded text-xs font-medium border transition-colors disabled:opacity-60 ${
                                        whole
                                          ? 'bg-heritage-blue text-white border-heritage-blue'
                                          : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-100'
                                      }`}
                                    >
                                      {fn.name}
                                    </button>
                                    {fn.departments.length > 0 && !whole && (
                                      <div className="mt-1 ml-4 flex flex-wrap gap-1.5">
                                        {fn.departments.map(d => {
                                          const on = areas.includes(d.value);
                                          return (
                                            <button
                                              key={d.value}
                                              onClick={() => toggleArea(d.value)}
                                              disabled={scopeSaving === '*'}
                                              title={`${d.positions} positions`}
                                              className={`px-2 py-0.5 rounded text-[11px] border transition-colors disabled:opacity-60 ${
                                                on
                                                  ? 'bg-heritage-blue text-white border-heritage-blue'
                                                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                                              }`}
                                            >
                                              {d.name}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <p className="text-[11px] text-gray-500 mt-2">
                              Choosing a function covers all its departments; choosing none leaves the
                              whole organisation visible. Downloads, gender and race stay with
                              administrators and HR administrators.
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* The access-change trail for this person */}
              <div className="border-t border-gray-200 pt-4">
                <button
                  type="button"
                  onClick={() => setHistoryOpen((o) => !o)}
                  className="text-sm font-semibold text-gray-700 hover:text-gray-900"
                >
                  {historyOpen ? '▾' : '▸'} Access history ({history.length})
                </button>
                {historyOpen && (
                  history.length === 0 ? (
                    <p className="text-xs text-gray-500 mt-2">No recorded changes yet.</p>
                  ) : (
                    <ul className="mt-2 space-y-2">
                      {history.map((h, i) => (
                        <li key={`${h.change_set_id}-${i}`} className="rounded border border-gray-100 bg-gray-50 px-3 py-2 text-xs">
                          <div className="flex flex-wrap justify-between gap-2">
                            <span className="font-medium text-gray-800">
                              {CHANGE_WORDS[h.change] || h.change}: {describeItem(h, permCatalog)}
                            </span>
                            <span className="text-gray-500">{formatWhen(h.occurred_at)}</span>
                          </div>
                          <div className="text-gray-600 mt-0.5">
                            {h.via === 'database' ? 'Changed outside the platform (no app record)' : `By ${h.actor}`}
                            {h.reason ? ` · ${h.reason}` : ''}
                            {h.reference ? ` · Ref ${h.reference}` : ''}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
